/* ============================================================
   Modal / wizard / form framework
   - Modal.open(<Content/>, sticky)  shows a dialog; Modal.close() hides it
   - <Frame title footer wide> the standard dialog chrome
   - useForm(init) + <FormCtx.Provider value={f}> + <F.Text k=…> for keyed form data
     (the old data-k="…" pattern: fields write straight into f.d[k])
   - startWizard(cfg) multi-step wizard with async validate / skipIf / onFinish
   - <Btn onClick={async…}> shows a spinner while the promise runs
   ============================================================ */
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createStore, hooks, toast, type Any } from "../lib/store";
import { copy } from "./common";

type ModalState = { el: ReactNode; sticky: boolean; key: number } | null;
const modal = createStore<ModalState>(null);
let mkey = 0;

export const Modal = {
  get sticky() { return !!modal.get()?.sticky; },
  isOpen: () => !!modal.get(),
  // clicking outside a dialog does not close it (it used to throw away half-filled wizards); use Cancel or ✕
  open(el: ReactNode, sticky = false) { modal.set({ el, sticky, key: ++mkey }); },
  close() { modal.set(null); },
  simple(title: ReactNode, body: ReactNode, action?: string, fn?: () => Any, wide?: boolean) {
    Modal.open(
      <Frame title={title} wide={wide} footer={<>
        <div className="spacer" />
        <button className="btn" onClick={Modal.close}>Cancel</button>
        {action && fn ? <Btn primary onClick={fn}>{action}</Btn> : null}
      </>}>{body}</Frame>,
    );
  },
  confirm(title: string, text: ReactNode, action: string, fn: () => Any) {
    Modal.open(
      <Frame title={title} footer={<>
        <div className="spacer" />
        <button className="btn" onClick={Modal.close}>Cancel</button>
        <button className="btn danger" onClick={() => { Modal.close(); fn(); }}>{action}</button>
      </>}><p style={{ marginTop: 0 }}>{text}</p></Frame>,
    );
  },
  showKey(key: string, note?: string, onDone?: () => void) {
    Modal.open(
      <Frame title="Copy your API key now" footer={<>
        <div className="spacer" />
        <button className="btn" onClick={() => copy(key)}>Copy</button>
        <button className="btn primary" onClick={() => { Modal.close(); onDone?.(); }}>Done</button>
      </>}>
        <p style={{ marginTop: 0 }}>This is the only time the full key is shown. {note || ""}</p>
        <pre className="log copy" style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }} onClick={() => copy(key)}>{key}</pre>
      </Frame>,
    );
  },
};
hooks.closeModal = Modal.close;

export function ModalRoot() {
  const m = modal.use();
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "Escape" && modal.get() && !modal.get()!.sticky) Modal.close(); };
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, []);
  return <div id="modal-root">{m ? <div className="scrim" key={m.key}>{m.el}</div> : null}</div>;
}

export function Frame({ title, children, footer, wide, className = "" }:
  { title: ReactNode; children?: ReactNode; footer?: ReactNode; wide?: boolean; className?: string }) {
  return (
    <div className={`modal ${wide ? "wide" : ""} ${className}`}>
      <div className="mh"><h3>{title}</h3><div className="spacer" /><button className="x" onClick={Modal.close}>✕</button></div>
      <div className="mb">{children}</div>
      <div className="mf">{footer ?? <><div className="spacer" /><button className="btn" onClick={Modal.close}>Close</button></>}</div>
    </div>
  );
}

/* ---------- async-aware button ---------- */
export function Btn({ onClick, children, primary, danger, sm, ghost, className = "", busyText = "Working…", disabled, title, id, type = "button" }:
  { onClick?: (e: React.MouseEvent) => Any; children: ReactNode; primary?: boolean; danger?: boolean; sm?: boolean; ghost?: boolean;
    className?: string; busyText?: string; disabled?: boolean; title?: string; id?: string; type?: "button" | "submit" }) {
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const cls = ["btn", primary && "primary", danger && "danger", sm && "sm", ghost && "ghost", className].filter(Boolean).join(" ");
  return (
    <button type={type} id={id} title={title} className={cls} disabled={disabled || busy}
      onClick={async (e) => {
        if (!onClick) return;
        const r = onClick(e);
        if (r && typeof r.then === "function") {
          setBusy(true);
          try { await r; } catch { /* callers toast their own errors */ } finally { if (alive.current) setBusy(false); }
        }
      }}>
      {busy ? <><span className="spin" /> {busyText}</> : children}
    </button>
  );
}

/* ---------- keyed form data ---------- */
export type Form = { d: Any; set: (k: string, v: Any) => void; refresh: () => void };
export const FormCtx = createContext<Form | null>(null);
export function useForm(init: Any = {}): Form {
  const ref = useRef<Any>(null);
  if (ref.current === null) ref.current = { ...init };
  const [, setN] = useState(0);
  const refresh = () => setN((n) => n + 1);
  return { d: ref.current, set: (k, v) => { ref.current[k] = v; refresh(); }, refresh };
}
const useF = () => {
  const f = useContext(FormCtx);
  if (!f) throw new Error("form field outside FormCtx");
  return f;
};
type Opt = string | number | [Any, ReactNode];
const optPair = (o: Opt): [Any, ReactNode] => (Array.isArray(o) ? o : [o, o]);
const Hint = ({ h }: { h?: ReactNode }) => (h ? <div className="hint">{h}</div> : null);

export const F = {
  Text({ k, label, hint, ph, type = "text", onChange, autoFocus, mono }:
    { k: string; label: ReactNode; hint?: ReactNode; ph?: string; type?: string; onChange?: (v: string) => void; autoFocus?: boolean; mono?: boolean }) {
    const f = useF();
    return (
      <label className="f"><span>{label}</span>
        <input type={type} value={f.d[k] ?? ""} placeholder={ph || ""} autoFocus={autoFocus} className={mono ? "mono" : undefined}
          autoComplete={type === "password" ? "new-password" : undefined}
          onChange={(e) => { f.set(k, e.target.value); onChange?.(e.target.value); }} />
        <Hint h={hint} /></label>
    );
  },
  Pass(p: { k: string; label: ReactNode; hint?: ReactNode; ph?: string }) { return <F.Text {...p} type="password" />; },
  Num({ k, label, hint, min, max, step }: { k: string; label: ReactNode; hint?: ReactNode; min?: number; max?: number; step?: number }) {
    const f = useF();
    return (
      <label className="f"><span>{label}</span>
        <input type="number" value={f.d[k] ?? ""} min={min} max={max} step={step} onChange={(e) => f.set(k, e.target.value)} />
        <Hint h={hint} /></label>
    );
  },
  Sel({ k, label, opts, hint, onChange }: { k: string; label: ReactNode; opts: Opt[]; hint?: ReactNode; onChange?: (v: string) => void }) {
    const f = useF();
    return (
      <label className="f"><span>{label}</span>
        <select value={f.d[k] ?? ""} onChange={(e) => { f.set(k, e.target.value); onChange?.(e.target.value); }}>
          {opts.map((o) => { const [v, t] = optPair(o); return <option key={String(v)} value={v}>{t}</option>; })}
        </select>
        <Hint h={hint} /></label>
    );
  },
  Check({ k, title, sub, onChange }: { k: string; title: ReactNode; sub?: ReactNode; onChange?: (v: boolean) => void }) {
    const f = useF();
    const on = !!f.d[k];
    return (
      <label className={"check " + (on ? "on" : "")}>
        <input type="checkbox" checked={on} onChange={(e) => { f.set(k, e.target.checked); onChange?.(e.target.checked); }} />
        <span><b>{title}</b><small>{sub || ""}</small></span></label>
    );
  },
  Area({ k, label, rows = 5, hint, mono = true, ph }: { k: string; label: ReactNode; rows?: number; hint?: ReactNode; mono?: boolean; ph?: string }) {
    const f = useF();
    return (
      <label className="f"><span>{label}</span>
        <textarea className={mono ? "plain" : undefined} rows={rows} spellCheck={false} value={f.d[k] ?? ""} placeholder={ph}
          onChange={(e) => f.set(k, e.target.value)} />
        <Hint h={hint} /></label>
    );
  },
};

/* ---------- generic wizard (async-aware) ---------- */
export type WizStep = {
  name: string;
  render: (d: Any, f: Form) => ReactNode;
  validate?: (d: Any) => Promise<string | null | undefined | void> | string | null | undefined | void;
  skipIf?: (d: Any) => boolean;
};
export type WizCfg = { title: ReactNode; steps: WizStep[]; data?: Any; finishLabel?: string; onFinish: (d: Any) => Promise<Any> | Any };

export function startWizard(cfg: WizCfg) { Modal.open(<Wizard cfg={cfg} />, true); }

function Wizard({ cfg }: { cfg: WizCfg }) {
  const f = useForm(cfg.data || {});
  const [i, setI] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const st = cfg.steps;
  // the current step is the last one when every later step is skipped for the current choices
  const isLast = () => { for (let k = i + 1; k < st.length; k++) if (!(st[k].skipIf && st[k].skipIf!(f.d))) return false; return true; };
  const last = isLast();
  const next = async () => {
    const s = st[i];
    if (s.validate) {
      setBusy("Checking…");
      let err: Any = null;
      try { err = await s.validate(f.d); } catch (e: Any) { err = e.message; }
      setBusy(null);
      if (err) { toast("Cannot continue", err, "err"); return; }
    }
    if (last) {
      setBusy("Checking…");
      try { await cfg.onFinish(f.d); } catch { /* onFinish toasts */ }
      setBusy(null);
      return;
    }
    let n = i + 1;
    while (st[n].skipIf && st[n].skipIf!(f.d)) n++;
    setI(n);
  };
  const back = () => { let n = i - 1; while (n > 0 && st[n].skipIf && st[n].skipIf!(f.d)) n--; setI(n); };
  return (
    <FormCtx.Provider value={f}>
      <div className="modal wide">
        <div className="mh"><h3>{cfg.title}</h3><div className="spacer" /><button className="x" onClick={Modal.close}>✕</button></div>
        <div className="steps">
          {st.map((s, n) => <div key={n} className={n === i ? "on" : n < i ? "done" : ""}>{n < i ? "✓ " : ""}{s.name}</div>)}
        </div>
        <div className="mb" id="wizbody">{st[i].render(f.d, f)}</div>
        <div className="mf">
          <span style={{ color: "var(--ink-3)", fontSize: 12.5 }}>Step {i + 1} of {st.length}</span><div className="spacer" />
          {i > 0 ? <button className="btn" onClick={back}>Back</button> : <button className="btn" onClick={Modal.close}>Cancel</button>}
          <button className="btn primary" id="wiznext" disabled={!!busy} onClick={next}>
            {busy ? <><span className="spin" /> {busy}</> : last ? cfg.finishLabel || "Deploy" : "Continue"}
          </button>
        </div>
      </div>
    </FormCtx.Provider>
  );
}

/* tabs inside dialogs / cards */
export function Tabs({ tabs, cur, onPick }: { tabs: [string, ReactNode][]; cur: string; onPick: (k: string) => void }) {
  return (
    <div className="tabs">
      {tabs.map(([k, l]) => <button key={k} className={cur === k ? "on" : ""} onClick={() => onPick(k)}>{l}</button>)}
    </div>
  );
}
